// Operator › Deploys (`/operator/deploys`; DC-04, 05 §DE configuration "Stage a deploy"): every
// deploy into the companies the agency operates (`GET /v1/operator/deploys`, one call, polled
// while one is moving), newest first, with its DE, company, template, kind and state. "Stage a
// deploy" builds the bundle from a published version and the DE's company override
// (`POST /v1/operator/deploys`); the server refuses a bundle with every reason, which the dialog
// lists. A deploy opens on its own page.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SearchField,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowUpRightIcon, LayersIcon, RocketIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { Deploy, MeResponse } from "../../../api/types";
import { formatDateTime } from "../../format";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  TABLE_ROW_HOVER,
} from "../../ops-ux";
import { LoadError, Loading } from "../../screen-parts";
import { problemLines } from "../templates/template-parts";
import { agentTemplateQuery, agentTemplatesQuery } from "../templates/template-queries";
import { awaitsApproval, companyName, DeployStateBadge, KIND_LABEL } from "./deploy-parts";
import { deployKeys, deploysQuery, deployTargetsQuery } from "./deploy-queries";

export function DeploysPage({ api, me }: { api: Api; me: MeResponse }) {
  const list = useQuery(deploysQuery(api));
  const targets = useQuery(deployTargetsQuery(api));
  const templates = useQuery(agentTemplatesQuery(api));
  const rows = list.data?.data ?? [];
  const deName = (id: string) =>
    workerName(
      targets.data?.data.find((r) => r.digital_employee.membership_id === id)?.digital_employee
        .display_name ?? `DE …${id.slice(-6)}`,
    );
  const templateName = (id: string) =>
    templates.data?.data.find((t) => t.id === id)?.name ?? `Template …${id.slice(-6)}`;
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? rows.filter((d) =>
        `${deName(d.de_membership_id)} ${companyName(me, d.target_company_id)} ${templateName(d.template_id)}`
          .toLowerCase()
          .includes(needle),
      )
    : rows;
  const waiting = rows.filter((d) => awaitsApproval(d)).length;
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Deploys" }]}
        icon={RocketIcon}
        title="Deploys"
        count={list.isPending ? undefined : rows.length}
        actions={<StageDialog api={api} me={me} />}
      />
      <Toolbar bleed>
        <SearchField
          placeholder="Search deploys…"
          aria-label="Search deploys"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
          {waiting} waiting for approval · a redeploy goes straight through
        </span>
      </Toolbar>
      <OpsBody>
        {list.isPending ? (
          <Loading label="Loading deploys" />
        ) : list.isError ? (
          <LoadError what="Deploys" error={list.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={RocketIcon}
            title="No deploys yet"
            description="Stage a deploy to build a DE's bundle from a published template version."
          />
        ) : (
          <Table data-slot="deploys">
            <TableCaption>Deploys into your agency's companies, newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Digital employee</TableHead>
                <TableHead scope="col">Company</TableHead>
                <TableHead scope="col">Template</TableHead>
                <TableHead scope="col">Kind</TableHead>
                <TableHead scope="col">State</TableHead>
                <TableHead scope="col">Staged</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((d) => (
                <DeployRow
                  key={d.id}
                  d={d}
                  de={deName(d.de_membership_id)}
                  company={companyName(me, d.target_company_id)}
                  template={templateName(d.template_id)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function DeployRow({
  d,
  de,
  company,
  template,
}: {
  d: Deploy;
  de: string;
  company: string;
  template: string;
}) {
  return (
    <TableRow data-deploy={d.id} data-state={d.state} className={TABLE_ROW_HOVER}>
      <TableHead scope="row" className="font-normal">
        <Link
          to="/operator/deploys/$deployId"
          params={{ deployId: d.id }}
          className="font-medium underline-offset-4 hover:underline"
        >
          {de}
        </Link>
      </TableHead>
      <TableCell>{company}</TableCell>
      <TableCell>{template}</TableCell>
      <TableCell>{KIND_LABEL[d.kind]}</TableCell>
      <TableCell>
        <DeployStateBadge deploy={d} />
        {d.error ? (
          <span className="mt-1 block text-xs text-danger-text">{d.error.code}</span>
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap">{formatDateTime(d.created_at)}</TableCell>
      <TableCell className="text-right">
        <RowActions>
          <RowLinkAction
            icon={ArrowUpRightIcon}
            label={`Open deploy of ${de}`}
            render={<Link to="/operator/deploys/$deployId" params={{ deployId: d.id }} />}
          />
          <RowLinkAction
            icon={LayersIcon}
            label={`Open template ${template}`}
            render={
              <Link to="/operator/templates/$templateId" params={{ templateId: d.template_id }} />
            }
          />
        </RowActions>
      </TableCell>
    </TableRow>
  );
}

/** "Stage a deploy": company → DE → template → published version, then the bundle is built. */
function StageDialog({ api, me }: { api: Api; me: MeResponse }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const companies = (me.agency?.grants ?? [])
    .filter((g) => g.level === "operator")
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
  const [companyId, setCompanyId] = useState("");
  const [deId, setDeId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [rollback, setRollback] = useState(false);
  const [key, setKey] = useState(newIdempotencyKey);
  const ids = { company: useId(), de: useId(), template: useId(), version: useId(), rb: useId() };

  const targets = useQuery({ ...deployTargetsQuery(api), enabled: open });
  const templates = useQuery({ ...agentTemplatesQuery(api), enabled: open });
  const versions = useQuery({
    ...agentTemplateQuery(api, templateId),
    enabled: open && !!templateId,
  });
  const des = (targets.data?.data ?? []).filter(
    (r) => r.company.id === companyId && r.digital_employee.status !== "retired",
  );
  // DC-05: a managed template still in draft may hold a pilot version (no verification skill).
  const deployable = (templates.data?.data ?? []).filter(
    (t) => t.kind === "persistent" && (t.status !== "draft" || t.template_class === "managed"),
  );
  const isManaged = versions.data?.template.template_class === "managed";
  const isPilot = (v: { state: string; verification_skill_pack_id: string | null }) =>
    isManaged && v.state === "draft" && v.verification_skill_pack_id === null;
  const choices = (versions.data?.versions ?? [])
    .filter(
      (v) =>
        v.state === "published" ||
        (rollback && v.state === "deprecated") ||
        (!rollback && isPilot(v)),
    )
    .sort((a, b) => b.version - a.version);

  const stage = useMutation({
    mutationFn: () =>
      api.client.operator.deploys.stage({
        idempotencyKey: key,
        body: {
          target_company_id: companyId,
          de_membership_id: deId,
          template_version_id: versionId,
          ...(rollback ? { rollback: true } : {}),
        },
      }),
    onSuccess: async (detail) => {
      await qc.invalidateQueries({ queryKey: deployKeys.all() });
      setOpen(false);
      void navigate({
        to: "/operator/deploys/$deployId",
        params: { deployId: detail.deploy.id },
      });
    },
    // A refused attempt is a new request next time (the key replays only the same request).
    onError: () => setKey(newIdempotencyKey()),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    stage.mutate();
  };
  const reset = () => {
    setCompanyId("");
    setDeId("");
    setTemplateId("");
    setVersionId("");
    setRollback(false);
    setKey(newIdempotencyKey());
    stage.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <Button size="sm" onClick={() => setOpen(true)}>
        <RocketIcon aria-hidden="true" />
        Stage a deploy
      </Button>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Stage a deploy</DialogTitle>
            <DialogDescription>
              Builds the DE's configuration bundle from a published version and the company's
              override. A first deploy of the version into the company then waits for approval.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.company}>Company</FieldLabel>
              <NativeSelect
                id={ids.company}
                required
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setDeId("");
                }}
              >
                <NativeSelectOption value="">Choose a company</NativeSelectOption>
                {companies.map((g) => (
                  <NativeSelectOption key={g.company_id} value={g.company_id}>
                    {g.company_name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.de}>Digital employee</FieldLabel>
              <NativeSelect
                id={ids.de}
                required
                value={deId}
                disabled={!companyId || targets.isPending}
                onChange={(e) => setDeId(e.target.value)}
              >
                <NativeSelectOption value="">
                  {companyId && !targets.isPending && des.length === 0
                    ? "No DEs in this company"
                    : "Choose a DE"}
                </NativeSelectOption>
                {des.map((r) => (
                  <NativeSelectOption
                    key={r.digital_employee.membership_id}
                    value={r.digital_employee.membership_id}
                  >
                    {workerName(r.digital_employee.display_name)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.template}>Template</FieldLabel>
              <NativeSelect
                id={ids.template}
                required
                value={templateId}
                disabled={templates.isPending}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setVersionId("");
                }}
              >
                <NativeSelectOption value="">Choose a template</NativeSelectOption>
                {deployable.map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.version}>Version</FieldLabel>
              <NativeSelect
                id={ids.version}
                required
                value={versionId}
                disabled={!templateId || versions.isPending}
                onChange={(e) => setVersionId(e.target.value)}
              >
                <NativeSelectOption value="">Choose a version</NativeSelectOption>
                {choices.map((v) => (
                  <NativeSelectOption key={v.id} value={v.id}>
                    v{v.version}
                    {v.state === "deprecated" ? " (deprecated)" : ""}
                    {isPilot(v) ? " (pilot: no verification skill)" : ""}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldDescription>
                Published versions deploy. A draft with no verification skill deploys only as a
                pilot, after an operator's verification override.
              </FieldDescription>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id={ids.rb}
                checked={rollback}
                onCheckedChange={(c) => {
                  setRollback(c === true);
                  setVersionId("");
                }}
              />
              <FieldLabel htmlFor={ids.rb} className="font-normal">
                Roll back to a version already confirmed in this company
              </FieldLabel>
            </Field>
            {stage.isError ? (
              <Notice tone="danger" role="alert" data-slot="stage-error">
                <ul className="flex flex-col gap-1">
                  {problemLines(stage.error, "The deploy could not be staged.").map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </Notice>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={stage.isPending || !companyId || !deId || !versionId}>
              {stage.isPending ? "Building the bundle…" : "Stage deploy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
